<script lang="ts">
  import { Ingresos, Laboratorio, Pacientes, type Ingreso, type OrdenLab } from '../../../lib/api';
  import { mapaNombresPaciente } from '../../../lib/nombres';

  export type TimelineItem = { hora: string; titulo: string; detalle: string; tipo: 'ingreso' | 'lab' | 'info' };

  let { limit = 10 }: { limit?: number } = $props();
  let items = $state<TimelineItem[]>([]);
  let error = $state<string | null>(null);

  function fmt(iso?: string | null) {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('es-GT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  }

  async function cargar() {
    error = null;
    try {
      const [ingresos, lab, pacs] = await Promise.all([
        Ingresos.activos(),
        Laboratorio.ordenes('VALIDADA').catch(() => [] as OrdenLab[]),
        Pacientes.list().catch(() => []),
      ]);
      const nombres = mapaNombresPaciente(pacs);
      const timeline: TimelineItem[] = [];
      for (const i of ingresos as Ingreso[]) {
        const quien = nombres[i.pacienteId] || 'Paciente';
        timeline.push({
          hora: fmt(i.ingresadoEn),
          titulo: 'Ingreso activo',
          detalle: i.diagnosticoIngreso ? `${quien} · ${i.diagnosticoIngreso}` : quien,
          tipo: 'ingreso',
        });
      }
      for (const o of (lab as OrdenLab[]).slice(0, 5)) {
        timeline.push({
          hora: fmt(o.facturadoEn),
          titulo: `Lab ${o.numero ?? ''}`.trim(),
          detalle: `${o.pacienteNombre ?? 'Paciente'} · validada`,
          tipo: 'lab',
        });
      }
      items = timeline.slice(0, limit);
    } catch (e: any) {
      error = e?.message ?? String(e);
    }
  }

  $effect(() => { cargar(); const t = setInterval(cargar, 60000); return () => clearInterval(t); });
</script>

<div class="panel">
  <header><h3>Actividad reciente</h3><button type="button" onclick={cargar}>↻</button></header>
  {#if error}<p class="err">{error}</p>{/if}
  {#if items.length === 0 && !error}<p class="empty">Sin actividad reciente.</p>{:else}
    <ol>
      {#each items as it, i (i)}
        <li class={it.tipo}>
          <time>{it.hora}</time>
          <div>
            <strong>{it.titulo}</strong>
            <span>{it.detalle}</span>
          </div>
        </li>
      {/each}
    </ol>
  {/if}
</div>

<style>
  .panel { border: 1px solid var(--border); border-radius: var(--radius-md); background: var(--bg-elevated); }
  header { display: flex; justify-content: space-between; padding: 0.65rem 0.85rem; border-bottom: 1px solid var(--border); }
  h3 { margin: 0; font-size: 0.9rem; }
  header button { border: 1px solid var(--border); background: var(--bg-surface); border-radius: 6px; cursor: pointer; padding: 0.2rem 0.5rem; }
  ol { list-style: none; margin: 0; padding: 0; max-height: 320px; overflow: auto; }
  li { display: flex; gap: 0.65rem; padding: 0.65rem 0.85rem; border-bottom: 1px solid var(--border); align-items: flex-start; }
  time { font-size: 0.72rem; color: var(--text-secondary); white-space: nowrap; min-width: 6.5rem; }
  strong { display: block; font-size: 0.85rem; color: var(--text-primary); }
  span { font-size: 0.78rem; color: var(--text-secondary); }
  li.ingreso strong { color: var(--danger); }
  li.lab strong { color: var(--info); }
  .empty, .err { padding: 0.85rem; font-size: 0.85rem; margin: 0; }
  .err { color: var(--danger); }
</style>
